// Core packages
import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { FormGroup } from "@angular/forms";

// Third party packages
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material";
import { FormlyFieldConfig, FormlyFormOptions } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";
import { MatProgressButtonOptions } from "mat-progress-buttons";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { Subscription } from "rxjs";
import { finalize } from "rxjs/operators";

// Custom packages
import { AuthService } from "src/app/modules/auth/auth.service";
import { IUser } from "src/app/shared/models/user/user.interface";
import { ApiService } from "src/app/shared/services/api.service";
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from "src/app/shared/services/helper.service";
import { TankService } from '../../tank.service';

/**
 * Script start
 */

@Component({
  selector: "app-add-document-to-tank-dialog",
  templateUrl: "./add-document-to-tank-dialog.component.html",
  styleUrls: ["./add-document-to-tank-dialog.component.scss"]
})
export class AddDocumentToTankDialogComponent
  implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  loggedUserPermissions: string[];
  lang = this.translateService.currentLang || this.translateService.defaultLang;
  dialogIsLoading = false; // Used to view/hide form loading spinner

  // BEGIN - Edit item form
  // BEGIN - ngx-formly
  form: FormGroup = new FormGroup({});
  model: any = {};
  fields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};
  // END - ngx-formly
  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.save"),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: "primary",
    spinnerColor: "primary",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-save",
      inline: true
    }
  };
  // END - Edit item form

  docUploaded = false;

  /**
   * Class constructor
   */
  constructor(
    public dialogRef: MatDialogRef<AddDocumentToTankDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private configService: ConfigService,
    private translateService: TranslateService,
    private authService: AuthService,
    private cookieService: CookieService,
    private toastrService: ToastrService,
    private helperService: HelperService,
    private tankService: TankService,
    private apiService: ApiService, // DO NOT MOVE since it's used for 'autocomplete' field type
  ) { }

  /**
   * Init components
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // Get logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe(res => (this.loggedUser = res))
    );
    this.subscriptions.push(
      this.authService.loggedUserPermissions$.subscribe(
        res => (this.loggedUserPermissions = res)
      )
    );

    // Get current lang
    this.lang = this.cookieService.get("lang") || this.configService.defaultLang;

    this.initForm();
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
   * Watch for file-uploader changes, if file was uploaded
   * scroll down on dialog bottom
   *
   * @since 1.0.0
   */
  watchFileChanges(): void {
    if (this.form.controls["documentFile"]) {
      this.subscriptions.push(
        this.form.controls["documentFile"].valueChanges.subscribe(
          res => {
            // A file was uploaded, scroll down dialog
            if (Array.isArray(res) && res.length) {
              const dialogWindow = document.getElementsByTagName("mat-dialog-container")[0]; // assuming we can have only one dialog opened
              if (dialogWindow) {
                const contentToScroll = dialogWindow.getElementsByClassName("mat-dialog-content");
                if (contentToScroll[0]) {
                  const bottomHeight = contentToScroll[0].scrollHeight;
                  contentToScroll[0].scrollTo({
                    top: bottomHeight,
                    behavior: "smooth",
                  });
                }
              }
            }
          },
          err => {
            this.helperService.handleError(err);
          }
        ));
    }
  }

  /**
   * Init form
   *
   * @since 1.0.0
   */
  initForm(docKey?: string): void {
    this.fields = [];
    this.model = {};
    if (this.form && this.form.controls) {
      for (const key in this.form.controls) {
        delete this.form.controls[key];
      }
    }
    this.fields = [
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-12",
            type: "input",
            key: "condo",
            templateOptions: {
              label: this.translateService.instant('tank.fields.condo'),
              placeholder: this.translateService.instant('tank.fields.condo'),
              minLength: 1,
              maxLength: 100,
            },
          },
          {
            className: "col-12",
            type: "input",
            key: "name",
            templateOptions: {
              label: this.translateService.instant('tank.fields.name'),
              placeholder: this.translateService.instant('tank.fields.name'),
              minLength: 1,
              maxLength: 100,
            },
          },
          {
            className: "col-12",
            type: "textarea",
            key: "description",
            templateOptions: {
              label: this.translateService.instant("tank.fields.description"),
              placeholder: this.translateService.instant("tank.fields.description"),
              required: false,
              minLength: 2,
              maxLength: 100,
              rows: 5,
            }
          },
          {
            className: "col-12",
            type: "file-uploader",
            key: "documentFile",
            templateOptions: {
              required: true,
              allowedFilesNumber: 10,
              allowedFilesExtensions: this.configService.allowedDocumentExtensions,
              customLabel: this.translateService.instant('tank.fields.file'),
            },
          },
        ]
      }
    ];
  }

  /**
   * Handle dialog closing
   *
   * @since 1.0.0
   */
  onClose(data?: any): void {
    // if (this.data) {
    //   if (this.data.item) {
    //     this.data.item.note = this.existingNotes;
    //   }
    // }
    this.dialogRef.close(data);
  }

  /**
   * Handle form submit
   *
   * @since 1.0.0
   */
  onFormSubmit(): void {
    // Prevent invalid data submit
    // if (this.model && !this.model.name) {
    //   this.toastrService.error("Devi scegliere un nome per il file prima di procedere");
    //   return;
    // }
    if (this.model && (!this.model.documentFile || (this.model.documentFile && !this.model.documentFile.length))) {
      this.toastrService.error("Devi caricare un file prima di procedere");
      return;
    }

    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach(key => {
        this.form.controls[key].markAsTouched();
      });
      console.warn("form", this.form);
      this.toastrService.error(
        this.translateService.instant("common.errors.form_is_invalid")
      );
      return;
    }

    // Activate submit button loading status
    this.formBtnOptions.active = true;

    this.subscriptions.push(
      this.tankService.createDoc(this.model)
        .pipe(finalize(() => (this.formBtnOptions.active = false)))
        .subscribe(
          res => {
            if (res.status) {
              if (res.data && typeof res.data === "string") {
                this.docUploaded = true;
              }
              this.toastrService.success(res.message);
              this.dialogRef.close({ save: true });
            }
          },
          err => {
            this.helperService.handleFormlyError(this.form, err);
          }
        )
    );
  }
}
